import { z } from 'zod';
import { FORM_FIELD_LIMITS } from '@/shared/utils/form-field-limits';
import {
  isValidAddressNumber,
  isValidBirthDate,
  isValidBrState,
  isValidCep,
  isValidCnpj,
  isValidCpf,
  isValidCurrency,
  isValidIssuingAgency,
  isValidLettersText,
  isValidNameText,
  isValidPlaceText,
  isValidRg,
} from './schema.formats';
import {
  CONTACT_TYPES,
  contactEntrySchema,
  createLocalizedContactEntry,
  type ContactType,
} from './schema.contacts';

export { CONTACT_TYPES, contactEntrySchema, type ContactType };

/** Schema for the personal info fields of a client. */
export const personalInfoSchema = z.object({
  fullName: z.string().min(1, 'Full name is required').max(120, 'Maximum 120 characters'),
  birthDate: z.string().default(''),
  maritalStatus: z.string().default(''),
  nationality: z.string().default(''),
  profession: z.string().default(''),
  gender: z.string().default(''),
  education: z.string().default(''),
  jobTitle: z.string().default(''),
  employer: z.string().default(''),
  industry: z.string().default(''),
});

/**
 * Schema for where the client came from. Write-once on the backend: only a
 * new client sends it; the edit form shows it read-only.
 */
export const originSchema = z.object({
  channel: z.string().default(''),
  sourceDetail: z.string().default(''),
  campaign: z.string().default(''),
});

/** Schema for the client address. */
export const addressSchema = z.object({
  street: z.string().max(FORM_FIELD_LIMITS.street).default(''),
  number: z.string().max(FORM_FIELD_LIMITS.number).default(''),
  complement: z.string().max(FORM_FIELD_LIMITS.complement).default(''),
  neighborhood: z.string().max(FORM_FIELD_LIMITS.neighborhood).default(''),
  city: z.string().max(FORM_FIELD_LIMITS.city).default(''),
  state: z.string().default(''),
  zipCode: z.string().default(''),
});

/** Schema for client financial data. */
export const financialSchema = z.object({
  monthlyIncome: z.string().default(''),
  incomeProof: z.string().default(''),
});

/** Schema for client property demand preferences. */
export const demandProfileSchema = z.object({
  businessType: z.string().default(''),
  cities: z.array(z.string()).default([]),
  neighborhoods: z.array(z.string()).default([]),
  propertyKinds: z.array(z.string()).default([]),
  priceMin: z.string().default(''),
  priceMax: z.string().default(''),
  bedroomsMin: z.string().default(''),
  areaMin: z.string().default(''),
  bathroomsMin: z.string().default(''),
  parkingMin: z.string().default(''),
  suitesMin: z.string().default(''),
  furnished: z.string().default(''),
  petFriendly: z.string().default(''),
  amenitiesInternal: z.array(z.string()).default([]),
  amenitiesExternal: z.array(z.string()).default([]),
  amenitiesSecurity: z.array(z.string()).default([]),
  condoLeisure: z.array(z.string()).default([]),
  condoSecurity: z.array(z.string()).default([]),
  tags: z.array(z.string()).default([]),
});

/** Schema for client metadata (notes, tags). */
export const metadataSchema = z.object({
  notes: z.string().max(FORM_FIELD_LIMITS.notes).default(''),
  tags: z.array(z.string()).default([]),
});

const individualIdentitySchema = z.object({
  personType: z.literal('individual'),
  cpf: z.string().default(''),
  rg: z.string().default(''),
  issuingAgency: z.string().default(''),
  cnpj: z.string().default(''),
  corporateName: z.string().default(''),
  tradeName: z.string().default(''),
});

const companyIdentitySchema = z.object({
  personType: z.literal('company'),
  cpf: z.string().default(''),
  rg: z.string().default(''),
  issuingAgency: z.string().default(''),
  cnpj: z.string().default(''),
  corporateName: z.string().default(''),
  tradeName: z.string().default(''),
});

const identitySchema = z.discriminatedUnion('personType', [
  individualIdentitySchema,
  companyIdentitySchema,
]);

/** Combined form schema for client drawer CRUD. */
export const clientFormSchema = z.object({
  personalInfo: personalInfoSchema,
  identity: identitySchema,
  roles: z.array(z.enum(['demand', 'supply'])).default(['demand']),
  /** E-mail of the agency member who answers for this client. */
  primaryBroker: z.string().default(''),
  /** `demand_profile.client_roles`: the client's roles, several at once. */
  clientRoles: z.array(z.string()).default([]),
  /** E-mail of the agency member who brought the client in. */
  capturedBy: z.string().default(''),
  /** `metadata.legacy_code`: the client's code in the previous system. */
  legacyCode: z.string().default(''),
  origin: originSchema.optional(),
  contacts: z.array(contactEntrySchema).min(1, 'At least one contact is required'),
  address: addressSchema.optional(),
  financial: financialSchema.optional(),
  demandProfile: demandProfileSchema.optional(),
  metadata: metadataSchema.optional(),
});

export type ClientFormData = z.infer<typeof clientFormSchema>;

type SchemaTranslate = (key: string, vars?: { max: number }) => string;

/** Minimum characters accepted in the full-name field. */
const FULL_NAME_MIN = 3;
/** Maximum characters accepted in the full-name field. */
const FULL_NAME_MAX = 120;
/** Maximum characters accepted in RG / issuing-agency fields. */
const DOCUMENT_TEXT_MAX = 20;
/** Maximum characters accepted in corporate / trade name fields. */
const COMPANY_NAME_MAX = 150;
/** Maximum characters accepted in the nationality field. */
const NATIONALITY_MAX = 60;
/** Maximum characters accepted in the profession field. */
const PROFESSION_MAX = 80;
/** Maximum characters accepted in the job title, employer and industry fields. */
const WORK_TEXT_MAX = 120;
/** Maximum characters accepted in the origin detail and campaign fields. */
const ORIGIN_TEXT_MAX = 120;
/** Maximum characters accepted in the previous system's code. */
const LEGACY_CODE_MAX = 60;

/**
 * Builds an optional string field that is validated only once filled.
 *
 * Blank passes (the drawer keeps these fields optional); a filled value must
 * satisfy `isValid` and stay within `max` characters.
 *
 * @param isValid - Format predicate, already blank-tolerant.
 * @param message - Localized message shown when the format check fails.
 * @param max - Optional character ceiling.
 * @param maxMessage - Localized message shown when `max` is exceeded.
 * @returns The Zod string schema for the field.
 */
function optionalField(
  isValid: (value: string) => boolean,
  message: string,
  max?: number,
  maxMessage?: string,
) {
  const base = max ? z.string().max(max, maxMessage) : z.string();
  return base.default('').superRefine((value, ctx) => {
    if (isValid(value)) return;
    ctx.addIssue({ code: z.ZodIssueCode.custom, message });
  });
}

/**
 * Free text with only a length ceiling (autocomplete fields, codes).
 *
 * @param max - Character ceiling.
 * @param t - Translator for the localized message.
 * @returns The Zod string schema.
 */
function boundedText(max: number, t: SchemaTranslate) {
  return z.string().max(max, t('form.validation.maxLength', { max })).default('');
}

/**
 * Builds the identity object for one person type, sharing the document
 * format rules across both branches of the discriminated union.
 */
function createIdentityBranch<T extends 'individual' | 'company'>(
  personType: T,
  t: SchemaTranslate,
) {
  const maxText = (max: number) => t('form.validation.maxLength', { max });
  const nameInvalid = t('form.validation.nameTextInvalid');
  return z.object({
    personType: z.literal(personType),
    cpf: optionalField(isValidCpf, t('form.validation.cpfInvalid')),
    rg: optionalField(isValidRg, t('form.validation.rgInvalid'), DOCUMENT_TEXT_MAX, maxText(DOCUMENT_TEXT_MAX)),
    issuingAgency: optionalField(
      isValidIssuingAgency,
      t('form.validation.issuingAgencyInvalid'),
      DOCUMENT_TEXT_MAX,
      maxText(DOCUMENT_TEXT_MAX),
    ),
    cnpj: optionalField(isValidCnpj, t('form.validation.cnpjInvalid')),
    corporateName: optionalField(isValidNameText, nameInvalid, COMPANY_NAME_MAX, maxText(COMPANY_NAME_MAX)),
    tradeName: optionalField(isValidNameText, nameInvalid, COMPANY_NAME_MAX, maxText(COMPANY_NAME_MAX)),
  });
}

/**
 * Address schema with a format check on every field.
 */
function createAddressSchema(t: SchemaTranslate) {
  const maxText = (max: number) => t('form.validation.maxLength', { max });
  const placeInvalid = t('form.validation.placeTextInvalid');
  const field = (isValid: (v: string) => boolean, message: string, max: number) =>
    optionalField(isValid, message, max, maxText(max));
  return z.object({
    street: field(isValidPlaceText, placeInvalid, FORM_FIELD_LIMITS.street),
    number: field(isValidAddressNumber, t('form.validation.addressNumberInvalid'), FORM_FIELD_LIMITS.number),
    complement: field(isValidPlaceText, placeInvalid, FORM_FIELD_LIMITS.complement),
    neighborhood: field(isValidPlaceText, placeInvalid, FORM_FIELD_LIMITS.neighborhood),
    city: field(isValidLettersText, t('form.validation.cityInvalid'), FORM_FIELD_LIMITS.city),
    state: optionalField(isValidBrState, t('form.validation.stateInvalid')),
    zipCode: optionalField(isValidCep, t('form.validation.zipCodeInvalid')),
  });
}

/**
 * Financial schema with a localized currency format check.
 */
function createFinancialSchema(t: SchemaTranslate) {
  return z.object({
    monthlyIncome: optionalField(isValidCurrency, t('form.validation.monthlyIncomeInvalid')),
    incomeProof: z.string().default(''),
  });
}

/**
 * Parses a masked pt-BR amount into a number for range comparison.
 */
function parseAmount(value: string): number | null {
  if (!value?.trim()) return null;
  const normalized = value.replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Demand-profile schema: currency format on both price bounds, plus a
 * cross-field check so the maximum cannot fall below the minimum.
 */
function createDemandProfileSchema(t: SchemaTranslate) {
  const currencyInvalid = t('form.validation.priceInvalid');
  return z.object({
    businessType: z.string().default(''),
    cities: z.array(z.string()).default([]),
    neighborhoods: z.array(z.string()).default([]),
    propertyKinds: z.array(z.string()).default([]),
    priceMin: optionalField(isValidCurrency, currencyInvalid),
    priceMax: optionalField(isValidCurrency, currencyInvalid),
    bedroomsMin: z.string().default(''),
    areaMin: z.string().default(''),
    bathroomsMin: z.string().default(''),
    parkingMin: z.string().default(''),
    suitesMin: z.string().default(''),
    furnished: z.string().default(''),
    petFriendly: z.string().default(''),
    amenitiesInternal: z.array(z.string()).default([]),
    amenitiesExternal: z.array(z.string()).default([]),
    amenitiesSecurity: z.array(z.string()).default([]),
    condoLeisure: z.array(z.string()).default([]),
    condoSecurity: z.array(z.string()).default([]),
    tags: z.array(z.string()).default([]),
  }).superRefine((data, ctx) => {
    const min = parseAmount(data.priceMin);
    const max = parseAmount(data.priceMax);
    if (min === null || max === null || max >= min) return;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t('form.validation.priceRangeInverted'),
      path: ['priceMax'],
    });
  });
}

/**
 * Builds the Zod schema with localized messages and per-field format checks.
 *
 * Called with a live `t` so messages resolve against the active language at
 * form-mount time rather than at module-evaluation time.
 *
 * @param t - Translator scoped to the `features/clients` namespace.
 * @returns The client form schema.
 */
export function createClientSchema(t: SchemaTranslate) {
  const localizedPersonalInfo = z.object({
    fullName: z.string()
      .trim()
      .min(1, t('form.validation.fullNameRequired'))
      .min(FULL_NAME_MIN, t('form.validation.fullNameMin'))
      .max(FULL_NAME_MAX, t('form.validation.maxLength', { max: FULL_NAME_MAX })),
    birthDate: optionalField(isValidBirthDate, t('form.validation.birthDateInvalid')),
    maritalStatus: z.string().default(''),
    nationality: optionalField(
      isValidLettersText,
      t('form.validation.lettersOnly'),
      NATIONALITY_MAX,
      t('form.validation.maxLength', { max: NATIONALITY_MAX }),
    ),
    profession: optionalField(
      isValidLettersText,
      t('form.validation.lettersOnly'),
      PROFESSION_MAX,
      t('form.validation.maxLength', { max: PROFESSION_MAX }),
    ),
    gender: z.string().default(''),
    education: z.string().default(''),
    jobTitle: boundedText(WORK_TEXT_MAX, t),
    employer: boundedText(WORK_TEXT_MAX, t),
    industry: boundedText(WORK_TEXT_MAX, t),
  });

  return z.object({
    personalInfo: localizedPersonalInfo,
    identity: z.discriminatedUnion('personType', [
      createIdentityBranch('individual', t),
      createIdentityBranch('company', t),
    ]),
    roles: z.array(z.enum(['demand', 'supply'])).default(['demand']),
    /** E-mail of the agency member who answers for this client. */
    primaryBroker: z.string().default(''),
    clientRoles: z.array(z.string()).default([]),
    capturedBy: z.string().default(''),
    legacyCode: boundedText(LEGACY_CODE_MAX, t),
    origin: z.object({
      channel: z.string().default(''),
      sourceDetail: boundedText(ORIGIN_TEXT_MAX, t),
      campaign: boundedText(ORIGIN_TEXT_MAX, t),
    }).optional(),
    contacts: z.array(createLocalizedContactEntry(t)).min(1, t('form.validation.contactRequired')),
    address: createAddressSchema(t).optional(),
    financial: createFinancialSchema(t).optional(),
    demandProfile: createDemandProfileSchema(t).optional(),
    metadata: metadataSchema.optional(),
  });
}

export { getClientTypeOptions, getBusinessTypeOptions } from './schema.options';

/**
 * Legacy preferencesSchema kept for backward compatibility during migration
 * of step components. Will be removed in Phase 6.
 */
export const preferencesSchema = demandProfileSchema;
export type ClientPreferences = z.infer<typeof demandProfileSchema>;
